import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import Layout from './components/Layout';
import { DashboardRouter } from './pages/DashboardRouter';
import Login from './pages/Login';
import { Signup } from './pages/Signup';
import { Landing } from './pages/Landing';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { FunctionCodeExamples } from './pages/FunctionCodeExamples';
import { FunctionRunHistory } from './pages/FunctionRunHistory';
import { FunctionRunDetails } from './pages/FunctionRunDetails';
import { IndividualRunDetails } from './pages/IndividualRunDetails';
import { ChainRunDetails } from './pages/ChainRunDetails';
import { ChainPerformancePage } from './pages/ChainPerformancePage';
import MyMaterials from './pages/MyMaterials';
import ApiKeyRuns from './pages/ApiKeyRuns';
import ApiKeys from './pages/ApiKeys';
import AdminCustomers from './pages/AdminCustomers';
import AdminCustomerHub from './pages/AdminCustomerHub';
import AdminCustomerApiKeys from './pages/AdminCustomerApiKeys';
import AdminUsageDashboard from './pages/AdminUsageDashboard';
import AdminSelfImprovement from './pages/AdminSelfImprovement';
import AdminPlatformRoutes from './pages/AdminPlatformRoutes';
import AdminInsights from './pages/AdminInsights';
import AdminSystemSettings from './pages/AdminSystemSettings';
import AdminBuildGuideFunnel from './pages/AdminBuildGuideFunnel';
import AdminAlertSettings from './pages/AdminAlertSettings';
import Insights from './pages/Insights';
import ModelStatus from './pages/ModelStatus';
import SelfImprovement from './pages/SelfImprovement';
import PrivateRoute from './components/PrivateRoute';
import { PlatformAdminRoute } from './components/PlatformAdminRoute';
import { ObjectBuilder } from './components/ObjectBuilder';
import { ObjectGenerator } from './pages/ObjectGenerator';
import { RequestLookup } from './pages/RequestLookup';
import { AIFunctionBuilder } from './components/AIFunctionBuilder';
import { AIFunctionList } from './components/AIFunctionList';
import TestLibrary from './pages/TestLibrary';
import TestRunPage from './pages/TestRunPage';
import TestsOverview from './pages/TestsOverview';
import Subscription from './pages/Subscription';
import { AuthProvider } from './contexts/AuthContext';
import { ViewModeProvider } from './contexts/ViewModeContext';
import { loadConfig } from './services/config';
import VerifyEmail from './pages/VerifyEmail';
import SubscriptionRoute from './components/SubscriptionRoute';
import { BuildGuide } from './pages/BuildGuide';
import { ToolBatchRunner } from './pages/ToolBatchRunner';
import { ChatbotStudio } from './pages/ChatbotStudio';
import { ChatbotEmbed } from './pages/ChatbotEmbed';
import { HtmlEditor } from './pages/HtmlEditor';
import { Playground } from './pages/Playground';
import { appTheme, embedTheme } from './theme';

function App() {
  useEffect(() => {
    // Load configuration on app startup
    loadConfig().then(config => {

    }).catch(err => {

    });
  }, []);

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <AuthProvider>
        <ViewModeProvider>
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/embed/chatbot" element={<ThemeProvider theme={embedTheme}><CssBaseline /><ChatbotEmbed /></ThemeProvider>} />
              
              {/* Protected Routes */}
              <Route path="/dashboard" element={
                <PrivateRoute>
                  <Layout>
                    <Box sx={{ p: 3 }}>
                      <DashboardRouter />
                    </Box>
                  </Layout>
                </PrivateRoute>
              } />
            <Route path="/dashboard/guide" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Build Guide">
                      <BuildGuide />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/wizard" element={<Navigate to="/dashboard/guide" replace />} />
            <Route path="/dashboard/api-keys/:id/runs" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="API Key Runs">
                      <ApiKeyRuns />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/objects" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <ObjectBuilder />
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/objects/generate" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Object Generator">
                      <ObjectGenerator />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/request-lookup" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Request Lookup">
                      <RequestLookup />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/ai-functions" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <AIFunctionBuilder />
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/ai-functions-list" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <AIFunctionList />
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/test-materials" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Test Library">
                      <TestLibrary />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/tests" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Tests">
                      <TestsOverview />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/tests/runs/:runId" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Test run">
                      <TestRunPage />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/ai-functions/:functionId/code" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <FunctionCodeExamples />
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/function-runs" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Runs & Performance">
                      <FunctionRunHistory />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/models" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Model Status">
                      <ModelStatus />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/function-runs/:functionId" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Run Details">
                      <FunctionRunDetails />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/run/:runId" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Run Details">
                      <IndividualRunDetails />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/chain-run/:executionId" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Chain Run Details">
                      <ChainRunDetails />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/chain-performance/:chainId" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Chain Performance">
                      <ChainPerformancePage />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/learning/:targetType/:targetId" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Self-improvement">
                      <SelfImprovement />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/playground" element={
              <PrivateRoute>
                <Layout>
                  <SubscriptionRoute pageTitle="Playground">
                    <Playground />
                  </SubscriptionRoute>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/materials" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Knowledge Base">
                      <MyMaterials />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/api-keys" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="API Keys">
                      <ApiKeys />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/subscription" element={
              <PrivateRoute>
                <Layout>
                  <Subscription />
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/tool-runner/:functionId" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Run on many files">
                      <ToolBatchRunner />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/chatbot/studio/:functionId" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <ChatbotStudio />
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/personal/*" element={<Navigate to="/dashboard" replace />} />
            
            {/* HTML Editor - no Layout wrapper, GrapeJS manages its own chrome */}
            <Route path="/dashboard/html-editor" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <HtmlEditor />
                  </Box>
                </Layout>
              </PrivateRoute>
            } />

            {/* Admin Routes */}
            <Route path="/dashboard/admin/customer-hub" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <AdminCustomerHub />
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/admin/customers" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <AdminCustomers />
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/admin/customers/:customerId/api-keys" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <AdminCustomerApiKeys />
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/admin/usage" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <AdminUsageDashboard />
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/admin/platform-routes" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <PlatformAdminRoute>
                      <AdminPlatformRoutes />
                    </PlatformAdminRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/admin/insights" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <PlatformAdminRoute>
                      <AdminInsights />
                    </PlatformAdminRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/insights" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <SubscriptionRoute pageTitle="Insights">
                      <Insights />
                    </SubscriptionRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/admin/learning" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <PlatformAdminRoute>
                      <AdminSelfImprovement />
                    </PlatformAdminRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/admin/system" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <PlatformAdminRoute>
                      <AdminSystemSettings />
                    </PlatformAdminRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/admin/build-guide" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <PlatformAdminRoute>
                      <AdminBuildGuideFunnel />
                    </PlatformAdminRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/admin/alerts" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <PlatformAdminRoute>
                      <AdminAlertSettings />
                    </PlatformAdminRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />

            {/* Platform Admin Only Routes */}
            <Route path="/dashboard/platform/customers" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <PlatformAdminRoute>
                      <div>Customer Management (TODO)</div>
                    </PlatformAdminRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/platform/usage" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <PlatformAdminRoute>
                      <div>Platform Usage (TODO)</div>
                    </PlatformAdminRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/dashboard/platform/billing" element={
              <PrivateRoute>
                <Layout>
                  <Box sx={{ p: 3 }}>
                    <PlatformAdminRoute>
                      <div>Billing Management (TODO)</div>
                    </PlatformAdminRoute>
                  </Box>
                </Layout>
              </PrivateRoute>
            } />
            
            {/* Catch-all route for 404 */}
            <Route path="*" element={
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <h1>404 - Page Not Found</h1>
                <p>The page you're looking for doesn't exist.</p>
              </Box>
            } />
          </Routes>
        </Router>
        </ViewModeProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
